import React, { useEffect, useMemo, useState } from 'react';
import CodeViewer from '../../components/CodeViewer';
import { useNavigate, useParams } from 'react-router-dom';
import { BreakGlassIncidentEntry, Pipeline } from '../../types';
import { apiGet, apiPost } from '../../utils/apiClient';

interface VersionRow {
  version_id: string;
  version: string;
  created_at: string;
  created_by_user_id?: string;
  created_by_username?: string;
  is_active?: string | boolean;
  change_notes?: string;
}

type BreakGlassEventRow = BreakGlassIncidentEntry;

const initialBreakGlassForm = {
  incident_reference: '',
  reason_summary: '',
  impact_description: '',
  customer_impact: '',
  mitigation_plan: '',
  rollback_plan: '',
};

export default function PipelineVersionsPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [pipeline, setPipeline] = useState<Pipeline | null>(null);
  const [versions, setVersions] = useState<VersionRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [sel, setSel] = useState<string[]>([]);
  const [comparePayload, setComparePayload] = useState<null | { left: any; right: any; v1: string; v2: string }>(null);
  const [compareOpen, setCompareOpen] = useState(false);
  const [breakGlassOpen, setBreakGlassOpen] = useState(false);
  const [breakGlassVersion, setBreakGlassVersion] = useState<VersionRow | null>(null);
  const [breakGlassSubmitting, setBreakGlassSubmitting] = useState(false);
  const [breakGlassForm, setBreakGlassForm] = useState(initialBreakGlassForm);
  const [breakGlassEvents, setBreakGlassEvents] = useState<BreakGlassEventRow[]>([]);
  const [breakGlassEventsLoading, setBreakGlassEventsLoading] = useState(false);
  const [selectedBreakGlassEvent, setSelectedBreakGlassEvent] = useState<BreakGlassEventRow | null>(null);

  const formatIdentity = (event: BreakGlassEventRow | null, prefix: 'activated' | 'created') => {
    if (!event) return 'Unknown';
    const name = event[`${prefix}_by_name` as keyof BreakGlassEventRow] as string | undefined;
    const username = event[`${prefix}_by_username` as keyof BreakGlassEventRow] as string | undefined;
    const email = event[`${prefix}_by_email` as keyof BreakGlassEventRow] as string | undefined;
    const userId = event[`${prefix}_by_user_id` as keyof BreakGlassEventRow] as string | undefined;
    const primary = name || username || email || userId || 'Unknown';
    const secondary = email && email !== primary ? ` (${email})` : '';
    return `${primary}${secondary}`;
  };

  useEffect(() => {
    if (!id) return;
    loadPipeline(id);
    loadVersions(id);
    loadBreakGlassEvents(id);
  }, [id]);

  const loadPipeline = async (pid: string) => {
    try {
      const r = await apiGet(`/api/pipelines/${pid}`);
      if (r.ok) setPipeline(await r.json());
    } catch {}
  };

  const loadVersions = async (pid: string) => {
    setLoading(true);
    try {
      const r = await apiGet(`/api/pipelines/${pid}/versions`);
      const data = await r.json();
      setVersions(data);
    } catch (e) {
      alert('Failed to load versions');
    } finally {
      setLoading(false);
    }
  };

  const loadBreakGlassEvents = async (pid: string) => {
    setBreakGlassEventsLoading(true);
    try {
      const r = await apiGet(`/api/pipelines/${pid}/break-glass-events`);
      const data = await r.json();
      setBreakGlassEvents(Array.isArray(data) ? data : []);
      setSelectedBreakGlassEvent((current) => {
        if (!Array.isArray(data) || data.length === 0) return null;
        return data.find((event: BreakGlassEventRow) => event.version_id === current?.version_id) || data[0];
      });
    } catch (e) {
      setBreakGlassEvents([]);
      setSelectedBreakGlassEvent(null);
    } finally {
      setBreakGlassEventsLoading(false);
    }
  };

  const restoreVersion = async (version: string) => {
    if (!id) return;
    if (!window.confirm(`Restore version v${version}? This will overwrite the current pipeline definition.`)) return;
    try {
      const res = await apiPost(`/api/pipelines/${id}/versions/${version}/restore`);
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.detail || 'Restore failed');
      await loadPipeline(id);
      await loadVersions(id);
      alert(`Restored to v${version}`);
    } catch (e: any) {
      alert(e?.message || 'Failed to restore version');
    }
  };

  const activateVersion = async (version: string) => {
    if (!id) return;
    if (!window.confirm(`Activate version v${version}? This will set the pipeline status to Active for this version.`)) return;
    try {
      const res = await apiPost(`/api/pipelines/${id}/versions/${version}/activate`);
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.detail || 'Activate failed');
      await loadPipeline(id);
      await loadVersions(id);
      alert(`Activated v${version}`);
    } catch (e: any) {
      alert(e?.message || 'Failed to activate version');
    }
  };

  const openBreakGlassModal = (versionRow: VersionRow) => {
    setBreakGlassVersion(versionRow);
    setBreakGlassForm(initialBreakGlassForm);
    setBreakGlassOpen(true);
  };

  const closeBreakGlassModal = () => {
    setBreakGlassOpen(false);
    setBreakGlassVersion(null);
    setBreakGlassForm(initialBreakGlassForm);
    setBreakGlassSubmitting(false);
  };

  const submitBreakGlassActivation = async () => {
    if (!id || !breakGlassVersion) return;
    setBreakGlassSubmitting(true);
    try {
      const res = await apiPost(
        `/api/pipelines/${id}/versions/${breakGlassVersion.version}/activate-break-glass`,
        breakGlassForm
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.detail || 'Break-glass activation failed');
      await loadPipeline(id);
      await loadVersions(id);
      await loadBreakGlassEvents(id);
      alert(`Emergency activation completed for v${breakGlassVersion.version}`);
      closeBreakGlassModal();
    } catch (e: any) {
      alert(e?.message || 'Break-glass activation failed');
      setBreakGlassSubmitting(false);
    }
  };

  const toggle = (v: string) => {
    setSel(prev => {
      const exists = prev.includes(v);
      let next = exists ? prev.filter(x => x !== v) : [...prev, v];
      if (next.length > 2) next = next.slice(next.length - 2);
      return next;
    });
  };

  const doCompare = async () => {
    if (!id) return;
    if (sel.length !== 2) {
      alert('Select exactly two versions to compare');
      return;
    }
    const [v1, v2] = sel;
    try {
      const [r1, r2] = await Promise.all([
        apiGet(`/api/pipelines/${id}/versions/${v1}`),
        apiGet(`/api/pipelines/${id}/versions/${v2}`),
      ]);
      if (!r1.ok || !r2.ok) throw new Error('Failed to load versions');
      const [left, right] = await Promise.all([r1.json(), r2.json()]);
      setComparePayload({ left, right, v1, v2 });
      setCompareOpen(true);
    } catch (e) {
      alert('Failed to compare versions');
    }
  };

  // Simple line compare: same/added/removed/modified by index
  const computeDiff = (a: string, b: string) => {
    const aLines = a.split('\n');
    const bLines = b.split('\n');
    const max = Math.max(aLines.length, bLines.length);
    const rows: { left?: string; right?: string; status: 'same'|'added'|'removed'|'modified' }[] = [];
    for (let i = 0; i < max; i++) {
      const L = aLines[i];
      const R = bLines[i];
      if (L === undefined && R !== undefined) {
        rows.push({ left: '', right: R, status: 'added' });
      } else if (L !== undefined && R === undefined) {
        rows.push({ left: L, right: '', status: 'removed' });
      } else if (L === R) {
        rows.push({ left: L, right: R, status: 'same' });
      } else {
        rows.push({ left: L ?? '', right: R ?? '', status: 'modified' });
      }
    }
    return rows;
  };

  const diffRows = useMemo(() => {
    if (!comparePayload) return [] as ReturnType<typeof computeDiff>;
    const leftStr = JSON.stringify(comparePayload.left, null, 2);
    const rightStr = JSON.stringify(comparePayload.right, null, 2);
    return computeDiff(leftStr, rightStr);
  }, [comparePayload]);

  const breakGlassReady = useMemo(
    () => Object.values(breakGlassForm).every(value => value.trim().length > 0),
    [breakGlassForm]
  );

  const badge = (v: VersionRow) => {
    const isActive = String(v.is_active) === 'true' || v.change_notes?.toLowerCase().includes('active');
    const isDraft = v.change_notes?.toLowerCase().includes('draft');
    const label = isActive ? 'Active' : isDraft ? 'Draft' : '';
    if (!label) return null;
    const color = isActive ? '#d1fae5' : '#fef3c7';
    const text = isActive ? '#065f46' : '#92400e';
    return (
      <span style={{ marginLeft: 8, background: color, color: text, borderRadius: 999, padding: '2px 8px', fontSize: 12, fontWeight: 600 }}>{label}</span>
    );
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div>
          <h2 style={{ margin: 0, color: '#1a202c' }}>Versions {pipeline ? `- ${pipeline.name}` : ''}</h2>
          <p style={{ margin: '6px 0 0 0', color: '#718096' }}>View, compare, and restore versions</p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={() => navigate(-1)} style={{ border: '1px solid #e5e7eb', background: 'white', borderRadius: 8, padding: '8px 12px' }}>Back</button>
          <button onClick={() => id && loadVersions(id)} style={{ border: '1px solid #e5e7eb', background: 'white', borderRadius: 8, padding: '8px 12px' }}>Refresh</button>
        </div>
      </div>

      <div style={{ background: '#ffffff', borderRadius: 12, padding: 16, border: '1px solid #e5e7eb' }}>
        <div style={{ marginBottom: 20 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
            <div>
              <h3 style={{ margin: 0, color: '#1f2937' }}>Break-Glass Events</h3>
              <p style={{ margin: '4px 0 0 0', color: '#6b7280', fontSize: 13 }}>
                Emergency activations are recorded separately so operators can inspect the incident context without digging through raw documents.
              </p>
            </div>
            <button onClick={() => id && loadBreakGlassEvents(id)} style={{ border: '1px solid #e5e7eb', background: 'white', borderRadius: 8, padding: '8px 12px' }}>
              Refresh Events
            </button>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'minmax(320px, 0.95fr) minmax(340px, 1.05fr)', gap: 12 }}>
            <div style={{ border: '1px solid #e5e7eb', borderRadius: 10, overflow: 'hidden' }}>
              <div style={{ padding: 12, borderBottom: '1px solid #e5e7eb', background: '#f9fafb', fontWeight: 700 }}>
                Event Timeline
              </div>
              {breakGlassEventsLoading ? (
                <div style={{ padding: 16, color: '#6b7280' }}>Loading break-glass events...</div>
              ) : breakGlassEvents.length === 0 ? (
                <div style={{ padding: 16, color: '#6b7280' }}>No break-glass events recorded for this pipeline.</div>
              ) : (
                <div style={{ maxHeight: 280, overflowY: 'auto' }}>
                  {breakGlassEvents.map(event => {
                    const isSelected = selectedBreakGlassEvent?.version_id === event.version_id;
                    return (
                      <button
                        key={`${event.version_id}-${event.activated_at || event.created_at}`}
                        onClick={() => setSelectedBreakGlassEvent(event)}
                        style={{
                          width: '100%',
                          textAlign: 'left',
                          border: 'none',
                          borderBottom: '1px solid #f3f4f6',
                          background: isSelected ? '#fff7ed' : 'white',
                          padding: 14,
                          cursor: 'pointer',
                        }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, marginBottom: 8 }}>
                          <div style={{ fontWeight: 700, color: '#1f2937' }}>v{event.version}</div>
                          <div style={{ fontSize: 12, color: '#6b7280' }}>
                            {new Date(event.activated_at || event.created_at).toLocaleString()}
                          </div>
                        </div>
                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
                          <span style={{ padding: '3px 8px', borderRadius: 999, background: '#ffedd5', color: '#9a3412', fontSize: 12, fontWeight: 700 }}>
                            Break Glass
                          </span>
                          {event.activated_by_username && (
                            <span style={{ padding: '3px 8px', borderRadius: 999, background: '#e0f2fe', color: '#075985', fontSize: 12, fontWeight: 700 }}>
                              {event.activated_by_email || event.activated_by_username}
                            </span>
                          )}
                        </div>
                        <div style={{ color: '#374151', fontSize: 13, fontWeight: 600, marginBottom: 4 }}>
                          {event.details.reason_summary || 'No reason summary'}
                        </div>
                        <div style={{ color: '#6b7280', fontSize: 12 }}>
                          Incident: {event.details.incident_reference || 'Not recorded'}
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            <div style={{ border: '1px solid #e5e7eb', borderRadius: 10, overflow: 'hidden' }}>
              <div style={{ padding: 12, borderBottom: '1px solid #e5e7eb', background: '#f9fafb', fontWeight: 700 }}>
                Event Details
              </div>
              {!selectedBreakGlassEvent ? (
                <div style={{ padding: 16, color: '#6b7280' }}>Select a break-glass event to inspect the stored incident details.</div>
              ) : (
                <div style={{ padding: 14 }}>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 10, marginBottom: 12 }}>
                    {[
                      ['Version', `v${selectedBreakGlassEvent.version}`],
                      ['Activated By', formatIdentity(selectedBreakGlassEvent, 'activated')],
                      ['Activated At', selectedBreakGlassEvent.activated_at ? new Date(selectedBreakGlassEvent.activated_at).toLocaleString() : 'Unknown'],
                      ['Incident Reference', selectedBreakGlassEvent.details.incident_reference || 'Not recorded'],
                      ['Version Author', formatIdentity(selectedBreakGlassEvent, 'created')],
                      ['Version Created', new Date(selectedBreakGlassEvent.created_at).toLocaleString()],
                    ].map(([label, value]) => (
                      <div key={label} style={{ background: '#f9fafb', borderRadius: 8, padding: 10 }}>
                        <div style={{ color: '#6b7280', fontSize: 12, marginBottom: 4 }}>{label}</div>
                        <div style={{ color: '#111827', fontSize: 13, fontWeight: 600, wordBreak: 'break-word' }}>{value}</div>
                      </div>
                    ))}
                  </div>

                  <div style={{ display: 'grid', gap: 10 }}>
                    {[
                      ['Reason Summary', selectedBreakGlassEvent.details.reason_summary],
                      ['Operational Impact', selectedBreakGlassEvent.details.impact_description],
                      ['Customer / Business Impact', selectedBreakGlassEvent.details.customer_impact],
                      ['Mitigation Plan', selectedBreakGlassEvent.details.mitigation_plan],
                      ['Rollback / Remediation Plan', selectedBreakGlassEvent.details.rollback_plan],
                    ].map(([label, value]) => (
                      <div key={label} style={{ background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: 8, padding: 12 }}>
                        <div style={{ color: '#9a3412', fontSize: 12, fontWeight: 700, marginBottom: 6 }}>{label}</div>
                        <div style={{ color: '#431407', fontSize: 13, whiteSpace: 'pre-wrap' }}>{value || 'Not recorded'}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <div style={{ borderTop: '1px solid #e5e7eb', marginBottom: 16 }} />

        {loading ? (
          <div>Loading...</div>
        ) : versions.length === 0 ? (
          <div style={{ color: '#6b7280' }}>No versions found</div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f9fafb' }}>
                <th style={{ textAlign: 'left', padding: 8, borderBottom: '1px solid #e5e7eb' }}>Select</th>
                <th style={{ textAlign: 'left', padding: 8, borderBottom: '1px solid #e5e7eb' }}>Version</th>
                <th style={{ textAlign: 'left', padding: 8, borderBottom: '1px solid #e5e7eb' }}>Created</th>
                <th style={{ textAlign: 'left', padding: 8, borderBottom: '1px solid #e5e7eb' }}>Author</th>
                <th style={{ textAlign: 'left', padding: 8, borderBottom: '1px solid #e5e7eb' }}>Notes</th>
                <th style={{ textAlign: 'left', padding: 8, borderBottom: '1px solid #e5e7eb' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {versions.map(v => (
                <tr key={v.version + v.version_id}>
                  <td style={{ padding: 8, borderBottom: '1px solid #f3f4f6' }}>
                    <input
                      type="checkbox"
                      checked={sel.includes(v.version)}
                      onChange={() => toggle(v.version)}
                    />
                  </td>
                  <td style={{ padding: 8, borderBottom: '1px solid #f3f4f6' }}>
                    <span style={{ fontWeight: 600 }}>v{v.version}</span>
                    {badge(v)}
                  </td>
                  <td style={{ padding: 8, borderBottom: '1px solid #f3f4f6' }}>{new Date(v.created_at).toLocaleString()}</td>
                  <td style={{ padding: 8, borderBottom: '1px solid #f3f4f6', color: '#6b7280' }}>{v.created_by_username || 'Unknown'}</td>
                  <td style={{ padding: 8, borderBottom: '1px solid #f3f4f6', color: '#6b7280' }}>{v.change_notes || ''}</td>
                  <td style={{ padding: 8, borderBottom: '1px solid #f3f4f6' }}>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button onClick={() => restoreVersion(v.version)} style={{ background: '#ef4444', color: 'white', border: 'none', borderRadius: 6, padding: '6px 10px' }}>Restore</button>
                      <button onClick={() => activateVersion(v.version)} style={{ background: '#10b981', color: 'white', border: 'none', borderRadius: 6, padding: '6px 10px' }}>Activate</button>
                      <button onClick={() => openBreakGlassModal(v)} style={{ background: '#7c2d12', color: 'white', border: 'none', borderRadius: 6, padding: '6px 10px' }}>Break Glass</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        <div style={{ marginTop: 12, display: 'flex', gap: 8 }}>
          <button
            onClick={doCompare}
            disabled={sel.length !== 2}
            style={{ background: '#111827', color: 'white', border: 'none', borderRadius: 8, padding: '8px 12px', opacity: sel.length !== 2 ? 0.6 : 1 }}
          >
            Compare selected
          </button>
          {sel.length > 0 && (
            <button onClick={() => setSel([])} style={{ border: '1px solid #e5e7eb', background: 'white', borderRadius: 8, padding: '8px 12px' }}>Clear selection</button>
          )}
        </div>
      </div>

      {compareOpen && comparePayload && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50 }}
          onClick={() => setCompareOpen(false)}
        >
          <div style={{ width: '90vw', maxWidth: 1100, maxHeight: '85vh', overflow: 'auto', background: 'white', borderRadius: 12, padding: 16 }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <div>
                <h3 style={{ margin: 0 }}>Compare v{comparePayload.v1} ↔ v{comparePayload.v2}</h3>
                <p style={{ margin: '4px 0 0 0', color: '#6b7280', fontSize: 12 }}>Side-by-side JSON with highlighted changes</p>
              </div>
              <button onClick={() => setCompareOpen(false)} style={{ border: '1px solid #e5e7eb', background: 'white', borderRadius: 8, padding: '6px 10px' }}>Close</button>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div>
                <div style={{ fontWeight: 600, marginBottom: 6 }}>v{comparePayload.v1}</div>
                <CodeViewer
                  value={diffRows.map((row) => row.left).join('\n')}
                  language="json"
                  theme="light"
                  height="500px"
                />
              </div>
              <div>
                <div style={{ fontWeight: 600, marginBottom: 6 }}>v{comparePayload.v2}</div>
                <CodeViewer
                  value={diffRows.map((row) => row.right).join('\n')}
                  language="json"
                  theme="light"
                  height="500px"
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {breakGlassOpen && breakGlassVersion && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 60 }}
          onClick={closeBreakGlassModal}
        >
          <div
            style={{ width: '90vw', maxWidth: 760, maxHeight: '88vh', overflow: 'auto', background: 'white', borderRadius: 12, padding: 20 }}
            onClick={e => e.stopPropagation()}
          >
            <div style={{ marginBottom: 16 }}>
              <h3 style={{ margin: 0 }}>Break-Glass Activation for v{breakGlassVersion.version}</h3>
              <p style={{ margin: '6px 0 0 0', color: '#6b7280', fontSize: 13 }}>
                This bypasses the normal second-person approval rule. Every field below is mandatory and will be stored with the emergency activation record.
              </p>
            </div>

            <div style={{ display: 'grid', gap: 12 }}>
              <div>
                <label style={{ display: 'block', fontWeight: 600, marginBottom: 6 }}>Incident / Ticket Reference</label>
                <input
                  value={breakGlassForm.incident_reference}
                  onChange={e => setBreakGlassForm(prev => ({ ...prev, incident_reference: e.target.value }))}
                  placeholder="e.g. INC-12345"
                  style={{ width: '100%', padding: 10, border: '1px solid #d1d5db', borderRadius: 8 }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontWeight: 600, marginBottom: 6 }}>Reason Summary</label>
                <textarea
                  value={breakGlassForm.reason_summary}
                  onChange={e => setBreakGlassForm(prev => ({ ...prev, reason_summary: e.target.value }))}
                  rows={3}
                  placeholder="Summarize why emergency activation is required"
                  style={{ width: '100%', padding: 10, border: '1px solid #d1d5db', borderRadius: 8, resize: 'vertical' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontWeight: 600, marginBottom: 6 }}>Operational Impact</label>
                <textarea
                  value={breakGlassForm.impact_description}
                  onChange={e => setBreakGlassForm(prev => ({ ...prev, impact_description: e.target.value }))}
                  rows={4}
                  placeholder="Describe what is currently broken or at risk"
                  style={{ width: '100%', padding: 10, border: '1px solid #d1d5db', borderRadius: 8, resize: 'vertical' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontWeight: 600, marginBottom: 6 }}>Customer / Business Impact</label>
                <textarea
                  value={breakGlassForm.customer_impact}
                  onChange={e => setBreakGlassForm(prev => ({ ...prev, customer_impact: e.target.value }))}
                  rows={3}
                  placeholder="Describe the customer or business impact"
                  style={{ width: '100%', padding: 10, border: '1px solid #d1d5db', borderRadius: 8, resize: 'vertical' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontWeight: 600, marginBottom: 6 }}>Immediate Mitigation Plan</label>
                <textarea
                  value={breakGlassForm.mitigation_plan}
                  onChange={e => setBreakGlassForm(prev => ({ ...prev, mitigation_plan: e.target.value }))}
                  rows={3}
                  placeholder="Describe the immediate mitigation steps"
                  style={{ width: '100%', padding: 10, border: '1px solid #d1d5db', borderRadius: 8, resize: 'vertical' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontWeight: 600, marginBottom: 6 }}>Rollback / Remediation Plan</label>
                <textarea
                  value={breakGlassForm.rollback_plan}
                  onChange={e => setBreakGlassForm(prev => ({ ...prev, rollback_plan: e.target.value }))}
                  rows={3}
                  placeholder="Describe rollback or follow-up remediation steps"
                  style={{ width: '100%', padding: 10, border: '1px solid #d1d5db', borderRadius: 8, resize: 'vertical' }}
                />
              </div>
            </div>

            <div style={{ marginTop: 16, padding: 12, borderRadius: 8, background: '#fff7ed', color: '#9a3412', fontSize: 13 }}>
              Emergency activation is disabled until all mandatory incident details are completed.
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 18 }}>
              <button onClick={closeBreakGlassModal} style={{ border: '1px solid #d1d5db', background: 'white', borderRadius: 8, padding: '8px 12px' }}>
                Cancel
              </button>
              <button
                onClick={submitBreakGlassActivation}
                disabled={!breakGlassReady || breakGlassSubmitting}
                style={{
                  border: 'none',
                  background: !breakGlassReady || breakGlassSubmitting ? '#cbd5e1' : '#7c2d12',
                  color: 'white',
                  borderRadius: 8,
                  padding: '8px 12px',
                  cursor: !breakGlassReady || breakGlassSubmitting ? 'not-allowed' : 'pointer'
                }}
              >
                {breakGlassSubmitting ? 'Submitting…' : 'Confirm Break Glass Activation'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
